import { orderSourceLabel } from '@/utils/orderSource';
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { getOrders } from '@/api/orders';
import { getDesktopSourceOptions, getSources } from '@/api/sources';
import { parseDeliveryDate, formatDateForBackend, formatDeliveryDateDisplay } from '../utils/dateUtils';
import OfflineInfoBar from '@/components/OfflineInfoBar';
import { format, startOfDay } from 'date-fns';
import StatusBadge, { getAllStatuses, getStatusConfig } from '@/components/StatusBadge';
import EditOrderDialog from '@/components/EditOrderDialog';
import OrderRow from '@/components/OrderRow';
import OrderFilters, { VIEW_FILTERS } from '@/components/OrderFilters';
import { Switch } from '@/components/ui/switch';
import ProtectedDeleteDialog from '@/components/ProtectedDeleteDialog';
import { useAllOrdersSession, boundedOrderRead } from '@/hooks/useAllOrdersSession';
import { createAllOrdersDecisions, warningIdentity } from '@/hooks/allOrdersDecisions';
import {
  canDeleteOrder,
  isProtectedFromDelete,
} from '@/utils/offlineDeletePolicy';
import { formatCurrency, sumOrderValueCents } from '@/utils/orderValue';
import { useOrderNetAmounts } from '@/hooks/useOrderNetAmounts';


// Browse windows are fetched server-side and bounded. We request CAP+1 so the
// client can tell whether the oldest day is complete, then day-trim to <= CAP
// (whole days only). Search fetches its own capped set across all history.
const BROWSE_WINDOW_CAP = 750;
const BROWSE_WINDOW_FETCH = BROWSE_WINDOW_CAP + 1;
const SEARCH_LIMIT = 750;


// A picked date range → inclusive {from,to} backend date strings. A single-click
// (no `to`) means that one day. Null when nothing is picked.
const rangeToParams = (range) => {
  if (!range?.from) return null;
  return {
    from: formatDateForBackend(range.from),
    to: formatDateForBackend(range.to || range.from),
  };
};
const rangeLabel = (range) => {
  const p = rangeToParams(range);
  if (!p) return null;
  const from = formatDeliveryDateDisplay(p.from);
  return p.to === p.from ? from : `${from} – ${formatDeliveryDateDisplay(p.to)}`;
};

const AllOrdersOpening = ({ session }) => {
  const { isAdmin, offline } = session;
  const [orders, setOrders] = useState([]);
  const [orderReadVersion, setOrderReadVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState('');
  const readRevision = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');   // search sent to the server
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [viewFilter, setViewFilter] = useState(VIEW_FILTERS.FUTURE);
  const [sortBy, setSortBy] = useState('date');
  const [dateRange, setDateRange] = useState(null);   // { from, to? } | null
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingOrder, setEditingOrder] = useState(null);
  const [protectedDeleteTarget, setProtectedDeleteTarget] = useState(null);
  const [armedDeleteOrderId, setArmedDeleteOrderId] = useState(null);
  // Tab-level print option. Resets to ON (include) on every page load by design —
  // numbers-present is the dependable default; opting out is a deliberate per-session act.
  const [includeOrderNumber, setIncludeOrderNumber] = useState(true);
  const [sources, setSources] = useState([]);                 // curated Source list for the filter
  const [selectedSourceIds, setSelectedSourceIds] = useState([]);
  const [sourceOptionsState, setSourceOptionsState] = useState('loading');
  const [sourceOptionsMode, setSourceOptionsMode] = useState(null);

  const sourceMode = offline ? 'offline' : 'online';

  // Get today's date in YYYY-MM-DD format for comparison
  const getTodayString = () => format(startOfDay(new Date()), 'yyyy-MM-dd');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const mounted = useRef(false);
  const sessionRef = useRef(session); sessionRef.current = session;
  const rowsRef = useRef(orders); rowsRef.current = orders;
  const editorRef = useRef(editingOrder); editorRef.current = editingOrder;
  const controllers = useRef(new Set());
  const arm = useRef(null);
  const protectedOpening = useRef(null);
  const read = (execute, config) => boundedOrderRead(execute, config, controllers.current);
  const capture = local => mounted.current ? sessionRef.current.capture(local) : null;
  const decisions = useMemo(() => createAllOrdersDecisions(capture, read), []);
  const decisionStates = useSyncExternalStore(decisions.subscribe, decisions.getSnapshot);
  const [commandNotice, setCommandNotice] = useState('');
  const [sourceRetry, setSourceRetry] = useState(0);
  const queryKey = JSON.stringify([viewFilter, dateRange, selectedSourceIds, debouncedSearch]);
  const latestQuery = useRef(queryKey); latestQuery.current = queryKey;
  const refresh = useRef(null);
  useLayoutEffect(() => {
    mounted.current = true;
    const pendingReads = controllers.current;
    decisions.activate();
    return () => {
      mounted.current = false;
      decisions.retire();
      pendingReads.forEach(controller => controller.abort());
      clearTimeout(arm.current?.timer);
      arm.current = null;
      protectedOpening.current = null;
    };
  }, [decisions]);

  // Every acquisition, including the accepted editor's refresh, has its own
  // query revision. The editor and list must both still own publication.
  const fetchOrders = async editorContext => {
    const owner = capture(false);
    if (!owner || (editorContext?.owns && !editorContext.owns())) {
      if (!editorContext?.owns) { setReadError('Order access is unavailable. Sign in or retry.'); setLoading(false); }
      return false;
    }
    const revision = ++readRevision.current;
    const owns = () => mounted.current && owner.current() && revision === readRevision.current
      && latestQuery.current === queryKey && (!editorContext?.owns || editorContext.owns());
    if (!editorContext?.owns) setLoading(true);
    setReadError('');
    try {
      const search = debouncedSearch.trim();
      const range = rangeToParams(dateRange);
      const params = { source_ids: selectedSourceIds };
      if (range) { params.date_from = range.from; params.date_to = range.to; }
      if (search) { params.search = search; params.limit = SEARCH_LIMIT; }
      else if (!range) { params.window = viewFilter; params.limit = BROWSE_WINDOW_FETCH; }
      const config = editorContext?.config || owner.config;
      const items = await read(request => {
        if (!owns()) throw new Error('Retired read');
        return getOrders({ ...params, working_view: true }, request);
      }, config);
      if (!owns()) return false;
      if (!Array.isArray(items) || items.some(item => !item || typeof item.id !== 'string')) throw new Error('Invalid orders');
      setOrders(items);
      setOrderReadVersion(value => value + 1);
      return true;
    } catch (error) {
      if (!owns()) return false;
      setReadError(selectedSourceIds.length
        ? 'Source-filtered orders could not be loaded. Your filter is still selected. Please retry.'
        : 'Orders could not be loaded. Please retry.');
      if (editorContext?.owns) throw error;
      return false;
    } finally {
      // Retirement of the editor alone must not strand this list's spinner.
      if (mounted.current && owner.current() && revision === readRevision.current) setLoading(false);
    }
  };
  refresh.current = fetchOrders;
  useEffect(() => {
    fetchOrders();
    return () => { readRevision.current += 1; };
  }, [queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const owner = capture(offline);
    let active = true;
    setSourceOptionsState('loading'); setSourceOptionsMode(sourceMode);
    const owns = () => active && mounted.current && owner?.current();
    if (!owner) { setSourceOptionsState('unavailable'); return; }
    read(config => {
      if (!owns()) throw new Error('Retired source read');
      return (offline ? getDesktopSourceOptions : getSources)(config);
    }, owner.config).then(items => {
      if (!owns()) return;
      if (!Array.isArray(items) || items.some(item => !item || typeof item.id !== 'string' || typeof item.name !== 'string')) throw new Error('Invalid sources');
      setSources(items.map(({ id, name }) => ({ id, name }))); setSourceOptionsState('ready');
    }).catch(() => { if (owns()) setSourceOptionsState('unavailable'); });
    return () => { active = false; };
  }, [offline, sourceMode, sourceRetry]);

  const invalidateRead = () => { readRevision.current += 1; setLoading(false); };
  const runDecision = async (order, action, options = {}) => {
    const owner = capture(false);
    const beforeWarning = warningIdentity(order);
    const currentTarget = () => rowsRef.current.find(row => row.id === order.id);
    if (!owner || editorRef.current?.id === order.id || decisions.blocked(order.id)
      || currentTarget() !== order || (options.current && !options.current())) return { state: 'blocked' };
    // Do not publish an already pending list observation over this decision.
    invalidateRead(); setCommandNotice('');
    const result = await decisions.run(order, action, options);
    if (!mounted.current || !owner.current()) return { state: 'retired' };
    invalidateRead();
    if (result.state === 'accepted') {
      setOrders(current => action === 'duplicate'
        ? current.map(row => row.id === order.id && warningIdentity(row) === beforeWarning
          ? { ...row, duplicate_warning: { ...row.duplicate_warning, resolution: options.resolution } } : row)
        : current.filter(row => row.id !== order.id));
      toast.success(result.message);
    } else if (result.message) setCommandNotice(result.message);
    // Current query observes independently if it changed during dispatch; do
    // not reinsert captured rows. A refresh failure never repeats the write.
    if (latestQuery.current !== queryKey || result.state === 'retired') void refresh.current();
    return result;
  };
  const handleDelete = orderId => {
    const order = rowsRef.current.find(row => row.id === orderId);
    if (!capture(false) || !order || decisions.blocked(orderId) || editorRef.current?.id === orderId
      || !canDeleteOrder(order, { isAdmin, offline })) return;
    if (arm.current?.id !== orderId) {
      clearTimeout(arm.current?.timer);
      const opening = { id: orderId, order };
      opening.timer = setTimeout(() => {
        if (arm.current === opening) { arm.current = null; setArmedDeleteOrderId(null); }
      }, 3000);
      arm.current = opening; setArmedDeleteOrderId(orderId); return;
    }
    const armed = arm.current;
    clearTimeout(armed.timer); arm.current = null; setArmedDeleteOrderId(null);
    if (armed.order !== order) { setCommandNotice('Order changed. Review it before deleting.'); return; }
    if (isProtectedFromDelete(order)) {
      const opening = { order };
      protectedOpening.current = opening; setProtectedDeleteTarget(opening); return;
    }
    void runDecision(order, 'delete');
  };
  const handleProtectedDelete = async (orderId, password) => {
    const opening = protectedOpening.current;
    if (!opening || opening.order.id !== orderId) return { state: 'blocked' };
    const result = await runDecision(opening.order, 'delete', { password, current: () => protectedOpening.current === opening });
    if (result.state === 'refused' || result.state === 'unknown' || result.state === 'blocked') {
      throw new Error(result.message || 'This order changed or is busy. Close and review it again.');
    }
    return result;
  };

  // The server already applied window / date / source / search. Client-side we
  // only narrow by status, then sort, day-trim, and group.
  const today = getTodayString();   // for the "Today (...)" group label only

  const statusFilteredOrders = useMemo(() => {
    if (statusFilter === 'all') return orders;
    return orders.filter(order => order.status === statusFilter);
  }, [orders, statusFilter]);

  // Determine sort direction based on view filter and sort type
  const sortDirection = useMemo(() => {
    if (sortBy === 'source') return 'asc';
    if (viewFilter === VIEW_FILTERS.FUTURE) return 'asc';
    return 'desc';
  }, [sortBy, viewFilter]);

  // Sort orders based on current sort settings
  const sortedOrders = useMemo(() => {
    return [...statusFilteredOrders].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'date') {
        const dateA = a.delivery_date || '';
        const dateB = b.delivery_date || '';
        comparison = dateA.localeCompare(dateB);
      } else if (sortBy === 'source') {
        const sourceA = (a.resolved_source || 'ZZZ').toLowerCase();
        const sourceB = (b.resolved_source || 'ZZZ').toLowerCase();
        comparison = sourceA.localeCompare(sourceB);
      }

      const directed = sortDirection === 'asc' ? comparison : -comparison;
      if (directed !== 0) return directed;

      // Tie-break within a group by internal order number (always ascending),
      // so renumbered orders actually move. Without this, same-day orders fall
      // back to their backend arrival order (creation time) and renumbering
      // appears to have no effect.
      const numA = a.internal_order_number || '';
      const numB = b.internal_order_number || '';
      return numA.localeCompare(numB);
    });
  }, [statusFilteredOrders, sortBy, sortDirection]);

  // Day-trim the windowed browse (Past/All, grouped by date) to whole days: walk
  // the date-desc list, include each whole day while the running total stays within
  // the cap; the first day that would exceed it is dropped, so the oldest date
  // shown is always complete. Skipped while searching, on a picked date, or when
  // grouping by source. (At this shop's volume no single day nears the cap, so the
  // newest day always fits.)
  const isWindowBrowse =
    (viewFilter === VIEW_FILTERS.PAST || viewFilter === VIEW_FILTERS.ALL) &&
    !dateRange?.from && !debouncedSearch.trim();
  const capApplies = isWindowBrowse && sortBy === 'date';
  const displayedOrders = useMemo(() => {
    if (!capApplies) return sortedOrders;
    const out = [];
    let i = 0;
    while (i < sortedOrders.length) {
      const day = sortedOrders[i].delivery_date || '';
      let j = i;
      while (j < sortedOrders.length && (sortedOrders[j].delivery_date || '') === day) j++;
      const dayCount = j - i;
      if (out.length === 0 || out.length + dayCount <= BROWSE_WINDOW_CAP) {
        for (let k = i; k < j; k++) out.push(sortedOrders[k]);
        i = j;
      } else {
        break;
      }
    }
    return out;
  }, [sortedOrders, capApplies]);

  const netAmounts = useOrderNetAmounts(displayedOrders, { enabled: !loading && !readError && sortBy === 'date', refreshKey: orderReadVersion });

  // Banner: the browse filled the server window, so older orders exist beyond it.
  const windowBanner = useMemo(() => {
    if (!isWindowBrowse || orders.length < BROWSE_WINDOW_FETCH) return null;
    const dates = displayedOrders.map(o => o.delivery_date).filter(Boolean);
    if (dates.length === 0) return null;
    let oldest = dates[0], newest = dates[0];
    for (const d of dates) { if (d < oldest) oldest = d; if (d > newest) newest = d; }
    return { oldest, newest };
  }, [isWindowBrowse, orders.length, displayedOrders]);

  // Group orders by current sort field for subheadings
  const groupedOrders = useMemo(() => {
    return displayedOrders.reduce((groups, order) => {
      const groupKey = sortBy === 'date' 
        ? (order.delivery_date || 'No Date')
        : (orderSourceLabel(order, 'Unknown Source'));
      
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(order);
      return groups;
    }, {});
  }, [displayedOrders, sortBy]);

  // Get ordered group keys
  const orderedGroupKeys = useMemo(() => {
    return Object.keys(groupedOrders).sort((a, b) => {
      if (sortBy === 'date') {
        if (a === 'No Date') return 1;
        if (b === 'No Date') return -1;
        return sortDirection === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
      } else {
        if (a === 'Unknown Source') return 1;
        if (b === 'Unknown Source') return -1;
        return sortDirection === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
      }
    });
  }, [groupedOrders, sortBy, sortDirection]);

  const toggleSort = (field) => {
    setSortBy(field);
  };

  const toggleExpand = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  // Format group header display
  const formatGroupHeader = (groupKey) => {
    if (sortBy === 'date') {
      if (groupKey === 'No Date') return 'No Delivery Date';
      // Check if it's today
      if (groupKey === today) {
        return `Today (${formatDeliveryDateDisplay(groupKey)})`;
      }
      return formatDeliveryDateDisplay(groupKey);
    }
    return groupKey;
  };

  // Get view filter description
  const getViewFilterDescription = () => {
    if (dateRange?.from) {
      return `Showing orders for ${rangeLabel(dateRange)}`;
    }
    switch (viewFilter) {
      case VIEW_FILTERS.FUTURE:
        return 'Showing orders for today and future dates';
      // Offline, past history reaches only as far back as the local cache. The
      // notice at the top of the screen states the exact boundary; these say
      // plainly that the list is not the shop's whole history.
      case VIEW_FILTERS.PAST:
        return offline
          ? 'Showing past orders saved on this computer'
          : 'Showing orders from past dates';
      default:
        return offline
          ? 'Showing all orders saved on this computer'
          : 'Showing all orders';
    }
  };

  // Get title based on filter state
  const getViewFilterTitle = () => {
    if (dateRange?.from) {
      return `Orders for ${rangeLabel(dateRange)}`;
    }
    switch (viewFilter) {
      case VIEW_FILTERS.FUTURE:
        return 'Upcoming Orders';
      case VIEW_FILTERS.PAST:
        return 'Past Orders';
      default:
        return 'All Orders';
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <OfflineInfoBar />
      <div className="p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-stone-900 mb-2">All Orders</h1>
          <p className="text-stone-600">View and manage all orders in the system</p>
        </div>

        {/* Search and Filters */}
        <Card data-testid="orders-search-card">
          <CardContent className="pt-6">
            {/* Other filters use the local order cache. The source control needs
                the accepted protected catalog before it can filter truthfully. */}
            <OrderFilters
              viewFilter={viewFilter}
              setViewFilter={setViewFilter}
              dateRange={dateRange}
              setDateRange={setDateRange}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              sortBy={sortBy}
              toggleSort={toggleSort}
              orderCount={displayedOrders.length}
              sources={sourceOptionsMode === sourceMode ? sources : []}
              sourceFilterStatus={sourceOptionsMode === sourceMode ? sourceOptionsState : 'loading'}
              sourceUnavailableMessage={offline ? undefined : 'Source choices unavailable. Orders remain visible; other filters still work.'}
              selectedSourceIds={selectedSourceIds}
              setSelectedSourceIds={setSelectedSourceIds}
            />
            {sourceOptionsState === 'unavailable' && <Button variant="outline" onClick={() => setSourceRetry(value => value + 1)}>Retry source choices</Button>}
            {/* Applies to protected originals, generated order sheets and trifold cards online
                and offline; offline originals are framed as unstamped copies. */}
            <div className="mt-4 pt-4 border-t border-stone-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">
                Print Options
              </p>
              <label
                htmlFor="include-internal-id"
                className="flex items-center gap-2 w-fit cursor-pointer"
                title="When on, the internal ID is added to printed trifold cards and to source documents and generated order sheets you view or print."
              >
                <Switch
                  id="include-internal-id"
                  checked={includeOrderNumber}
                  onCheckedChange={setIncludeOrderNumber}
                  data-testid="toggle-include-internal-id"
                />
                <span className="text-sm text-stone-700">Internal ID</span>
              </label>
            </div>
          </CardContent>
        </Card>

        {commandNotice && <p role="status">{commandNotice}</p>}
        {decisionStates.map(item => <div key={item.id} role="status" className="rounded border p-3" data-testid={`order-decision-${item.id}`}>
          <p>{item.state === 'pending' ? item.stalled ? 'Request still pending. Do not repeat it; you may leave this screen.' : 'Order action pending…'
            : 'Order action result not confirmed.'}</p>
          {item.review && <p>{item.review}</p>}
          {isAdmin && item.state === 'unknown' && <>
            <Button variant="outline" onClick={() => decisions.review(item.id)}>Check current order</Button>
            {(item.retry || item.complete) && <Button variant="outline" onClick={async () => {
              // Keep custody when the visible list cannot acquire current facts.
              if (await refresh.current()) decisions.acknowledge(item.id);
            }}>{item.retry ? 'I reviewed the uncertainty; allow a new decision' : 'Use the observed state'}</Button>}
          </>}
        </div>)}
        {/* Orders List */}
        <Card data-testid="all-orders-list-card">
          <CardHeader>
            <CardTitle>{getViewFilterTitle()}{!readError && ` (${displayedOrders.length})`}</CardTitle>
            <CardDescription>
              {getViewFilterDescription()}
              {searchTerm && ` • Filtered by "${searchTerm}"`}
              {windowBanner && ` • Showing ${formatDeliveryDateDisplay(windowBanner.oldest)} – ${formatDeliveryDateDisplay(windowBanner.newest)} · search or pick a date to see older orders`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-stone-500">Loading orders...</p>
              </div>
            ) : readError ? (
              <div role="alert" className="text-center py-8">
                <p>{readError}</p>
                <Button variant="outline" onClick={() => fetchOrders()}>Retry orders</Button>
              </div>
            ) : sortedOrders.length === 0 ? (
              <p className="text-stone-500 text-center py-8" data-testid="no-orders-message">
                {searchTerm 
                  ? 'No orders match your search.' 
                  : dateRange?.from
                    ? `No orders for ${rangeLabel(dateRange)}.`
                    : viewFilter === VIEW_FILTERS.FUTURE 
                      ? 'No upcoming orders.' 
                      : viewFilter === VIEW_FILTERS.PAST 
                        ? 'No past orders.' 
                        : 'No orders yet. Upload documents to get started.'}
              </p>
            ) : (
              <div className="space-y-4">
                {orderedGroupKeys.map((groupKey) => (
                  <div key={groupKey} className="space-y-1">
                    {/* Group Header */}
                    <div className="sticky top-0 bg-stone-100 px-3 py-2 rounded-lg border-l-4 border-primary flex items-center justify-between">
                      <span className="font-semibold text-stone-700">
                        {formatGroupHeader(groupKey)}
                      </span>
                      <div className="flex items-center justify-end gap-2 text-sm text-stone-500 text-right">
                        <span>
                          {groupedOrders[groupKey].length} order{groupedOrders[groupKey].length !== 1 ? 's' : ''}
                        </span>
                        {sortBy === 'date' && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span
                              className="font-semibold text-stone-700 tabular-nums"
                              data-testid={`order-group-total-${groupKey}`}
                            >
                              {formatCurrency(sumOrderValueCents(groupedOrders[groupKey]) / 100)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Orders in this group */}
                    {groupedOrders[groupKey].map((order) => (
                      <OrderRow
                        key={order.id}
                        netAmount={netAmounts.forOrder(order)}
                        onRetryNet={netAmounts.retry}
                        order={order}
                        isExpanded={expandedOrderId === order.id}
                        onToggleExpand={toggleExpand}
                        onEdit={target => { if (capture(false) && !decisions.blocked(target.id)) setEditingOrder(target); }}
                        actionPending={decisions.blocked(order.id) || editingOrder?.id === order.id}
                        onDuplicateDecision={isAdmin && !offline && !editingOrder ? (target, resolution, current) => runDecision(target, 'duplicate', { resolution, current }) : undefined}
                        onDelete={
                          canDeleteOrder(order, { isAdmin, offline })
                            ? handleDelete
                            : undefined
                        }
                        deleteArmed={armedDeleteOrderId === order.id}
                        sortBy={sortBy}
                        includeOrderNumberOnPrint={includeOrderNumber}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <EditOrderDialog
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={(_orderId, context) => fetchOrders(context)}
        />

        <ProtectedDeleteDialog
          key={protectedDeleteTarget?.order.id || 'closed'}
          opening={protectedDeleteTarget}
          isOpen={!!protectedDeleteTarget}
          onClose={() => { protectedOpening.current = null; setProtectedDeleteTarget(null); }}
          onConfirm={handleProtectedDelete}
          order={protectedDeleteTarget?.order}
        />
      </div>
      </div>
    </div>
  );
};

const AllOrdersView = () => {
  const session = useAllOrdersSession();
  return session.valid ? <AllOrdersOpening key={session.key} session={session} />
    : <div role="status">Order access is unavailable. Sign in to view orders.</div>;
};
export default AllOrdersView;
